import { memo } from "react";
import { Draggable } from "react-beautiful-dnd";
import { useSelector } from "react-redux";

import { TableRow } from "@mui/material";

import { Loading } from "classes/Loading";

import { LineDragInsertCopy } from "components/formFields/line/LineDragInsertCopy";
import { EditLineButton } from "components/utility/EditLineButton";

import { copyDown } from "services/utility/lineItems";

import { LineItem } from "views/Adjustments/Adjustment/LineItem";

// Memoized row component to prevent unnecessary re-renders
const MemoizedLineItem = memo(
  ({
    line,
    index,
    record,
    objectType,
    classes,
    classesCount,
    location,
    lineHandler,
    expandItemGroup,
    items,
    itemsCount,
    addItem,
    jobWorkcenters,
    lineItemsErrors,
    insertEmptyLine,
    isLastLine,
    lineReadOnly = false,
    nextLineReadOnly,
    setLineEdit,
    handleDeleteLine,
  }) => {
    const valueDiffLoading = line.valueDiff instanceof Loading;
    const linesLoading = useSelector((state) => state.loading.linesLoading);
    return (
      <Draggable
        key={index}
        draggableId={`${index}`}
        index={index}
        isDragDisabled={linesLoading}
      >
        {(draggableProvided, snapshot) => {
          return (
            <TableRow
              ref={draggableProvided.innerRef}
              {...draggableProvided.draggableProps}
              sx={{
                ...draggableProvided.draggableProps.style,
                position: "inherit",
                backgroundColor: snapshot.isDragging
                  ? "dragBackground"
                  : "none",
              }}
              data-testing={"line-" + index}
            >
              <LineDragInsertCopy
                draggableProvided={draggableProvided}
                snapshot={snapshot}
                insertEmptyLine={insertEmptyLine}
                lineNumber={line.lineNumber}
                onCopyDown={() => copyDown(line, lineHandler, objectType)}
                disabled={valueDiffLoading}
                showCopyDown={!isLastLine}
                toggleLineEdit={setLineEdit}
                lineIndex={index}
                nextLineReadOnly={nextLineReadOnly}
              />
              <EditLineButton
                onClick={setLineEdit}
                lineIndex={index}
                recordId={record.id}
                lineReadOnly={lineReadOnly}
              />

              <LineItem
                objectType={objectType}
                record={record}
                classes={classes}
                classesCount={classesCount}
                location={location}
                line={line}
                lineHandler={lineHandler}
                expandItemGroup={expandItemGroup}
                items={items}
                itemsCount={itemsCount}
                addItem={addItem}
                jobWorkcenters={jobWorkcenters}
                lineItemsErrors={lineItemsErrors}
                draggableProvided={draggableProvided}
                draggableSnapshot={snapshot}
                lineReadOnly={lineReadOnly}
                handleDeleteLine={handleDeleteLine}
              />
            </TableRow>
          );
        }}
      </Draggable>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison to prevent unnecessary re-renders
    return (
      prevProps.line === nextProps.line &&
      prevProps.index === nextProps.index &&
      prevProps.lineItemsErrors === nextProps.lineItemsErrors &&
      prevProps.isLastLine === nextProps.isLastLine &&
      prevProps.items === nextProps.items &&
      prevProps.classes === nextProps.classes &&
      prevProps.jobWorkcenters === nextProps.jobWorkcenters &&
      prevProps.location === nextProps.location &&
      prevProps.lineReadOnly === nextProps.lineReadOnly &&
      prevProps.nextLineReadOnly === nextProps.nextLineReadOnly
    );
  }
);

// helps React DevTools and error messages show a readable name
MemoizedLineItem.displayName = "MemoizedLineItem";

export { MemoizedLineItem };
